import { useMutation, useQuery } from "@apollo/client";
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Box,
  Button,
  Center,
  Divider,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Heading,
  Image,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalOverlay,
  Spinner,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Tag,
  Text,
  useColorMode,
  useDisclosure,
  useToast,
  VStack,
} from "@chakra-ui/react";
import { useCallback, useEffect, useRef, useState } from "react";

import { ISaleAdmin } from "../../../services/graphql/Sale";
import { SALES_ADMIN } from "../../../services/graphql/query/Sale";
import { useUnauthorizedAction } from "../../../services/hooks/useUnauthorizedAction";

import { TableSales } from "./TableSales";
import { ISaleProduct } from "../../../services/graphql/SaleProduct";
import { FormAddShipment } from "./FormAddShipment";
import { useForm } from "react-hook-form";
import moment from "moment";
import { MessageError } from "../../../shared/MessageError";
import { GET_STATUS_PAY } from "../../../services/graphql/mutation/MercadoPago";
import {
  CREATE_SHIPMENT,
  DELETE_SHIPMENT,
  UPDATE_SHIPMENT,
} from "../../../services/graphql/mutation/Shipment";
import { MessageSuccess } from "../../../shared/MessageSuccess";
import { IShipment } from "../../../services/graphql/Shipment";

export const Sales = () => {
  const { colorMode } = useColorMode();
  const [loading, setLoading] = useState<boolean>(false);
  const [dataSales, setDataSalesActive] = useState<ISaleAdmin[]>([]);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [saleToDelete, setSaleToDelete] = useState<
    { id: number; refImg: string } | undefined
  >();
  const [startDate, setstartDate] = useState<Date>(new Date());
  const [dataSalesWithoutShipment, setDataSalesWithoutShipment] = useState<
    ISaleAdmin[]
  >([]);
  const [dataSalesPending, setDataSalesPending] = useState<ISaleAdmin[]>([]);
  const [formAddShipmentVisible, setFormAddShipmentVisible] =
    useState<boolean>(false);
  const [modalViewProductVisible, setModalViewProductVisible] =
    useState<boolean>(false);
  const [productsSales, setProductsSales] = useState<ISaleProduct[]>([]);
  const [shipmentToEdit, setshipmentToEdit] = useState<IShipment>();
  const [getStatusPay] = useMutation(GET_STATUS_PAY);
  const [createShipment] = useMutation(CREATE_SHIPMENT);
  const [updateShipment] = useMutation(UPDATE_SHIPMENT);
  const [deleteShipment] = useMutation(DELETE_SHIPMENT);
  const [shipmentToDelete, setShipmentToDelete] = useState<number>();
  const [loadingButtons, setloadingButtons] = useState<boolean>(false);
  const [saleId, setSaleId] = useState<number>();

  //   const [deleteSale] = useMutation(DELETE_SALE);
  const [urlImg, setUrlImg] = useState<string>("");
  const cancelRef = useRef<any>();
  const { fetchMore } = useQuery(SALES_ADMIN);
  const { action } = useUnauthorizedAction();
  const toast = useToast();
  const {
    handleSubmit,
    register,
    formState: { errors, isSubmitting },
  } = useForm();

  const unauthorized = useCallback(() => {
    action();
  }, []);

  const sales = () => {
    setLoading(true);
    fetchMore({})
      .then((dataSales) => {
        const { data } = dataSales;
        const { salesAdmin } = data;

        setDataSalesActive(() => salesAdmin);
        setDataSalesWithoutShipment(() =>
          salesAdmin.filter((ele: any) => !ele.shipment)
        );
        setDataSalesPending(() =>
          salesAdmin.filter((ele: any) => ele.sale_status === "Pending")
        );
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  };
  const reanalyzePay = (sale_id: number) => {
    setloadingButtons(true);
    getStatusPay({
      variables: {
        input: {
          sale_id,
        },
      },
    })
      .then(() => {
        sales();
      })
      .catch(() => {
        toast({
          duration: 2500,
          position: "top",
          render: () => MessageError("No pudimos reanalizar el pago."),
        });
      })
      .finally(() => {
        setloadingButtons(false);
      });
  };

  useEffect(() => {
    sales();
  }, []);

  const onCloseWithCleanState = () => {
    setSaleToDelete(undefined);
    onClose();
  };

  const onSubmitCreateShipment = (values: any) => {
    if (moment(values.shipment_date).isBefore(new Date(), "day")) {
      toast({
        duration: 2500,
        position: "top",
        render: () =>
          MessageError("La fecha de envío no puede ser menor a hoy."),
      });
      return;
    }
    if (!saleId) {
      toast({
        duration: 2500,
        position: "top",
        render: () =>
          MessageError(
            "No pudímos crear el envío porque no tenemos referencia a la venta del mismo."
          ),
      });
      return;
    }
    setloadingButtons(true);
    createShipment({
      variables: {
        input: {
          sale_id: saleId,
          shipment_date: new Date(values.shipment_date),
        },
      },
    })
      .then(async () => {
        toast({
          duration: 2500,
          position: "bottom",
          render: () =>
            MessageSuccess({
              title: "Bien hecho!",
              description: "El envío se registró exitosamente!.",
            }),
        });
        sales();
        setFormAddShipmentVisible(false);
      })
      .catch((error) => {
        if (error.message !== "Unauthorized") {
          toast({
            duration: 2500,
            position: "top",
            render: () =>
              MessageError(
                "Ocurrió un error al crear su envío. Por favor intente nuevamente."
              ),
          });
        } else {
          unauthorized();
        }
      })
      .finally(() => {
        setloadingButtons(false);
      });
  };

  const onSubmitUpdateShipment = (values: any) => {
    if (moment(values.shipment_date).isBefore(new Date(), "day")) {
      toast({
        duration: 2500,
        position: "top",
        render: () =>
          MessageError("La fecha de envío no puede ser menor a hoy."),
      });
      return;
    }
    if (!shipmentToEdit) {
      toast({
        duration: 2500,
        position: "top",
        render: () =>
          MessageError(
            "No pudímos crear el envío porque no tenemos referencia al mismo."
          ),
      });
      return;
    }
    setloadingButtons(true);
    updateShipment({
      variables: {
        input: {
          id: shipmentToEdit.id,
          shipment_date: new Date(values.shipment_date),
        },
      },
    })
      .then(async () => {
        toast({
          duration: 2500,
          position: "bottom",
          render: () =>
            MessageSuccess({
              title: "Bien hecho!",
              description: "El envío se actualizó exitosamente!.",
            }),
        });
        sales();
        setFormAddShipmentVisible(false);
      })
      .catch((error) => {
        if (error.message !== "Unauthorized") {
          toast({
            duration: 2500,
            position: "top",
            render: () =>
              MessageError(
                "Ocurrió un error al actualizar su envío. Por favor intente nuevamente."
              ),
          });
        } else {
          unauthorized();
        }
      })
      .finally(() => {
        setloadingButtons(false);
      });
  };

  const onSubmitDeleteShipment = (shipment_id: number) => {
    setloadingButtons(true);
    deleteShipment({
      variables: {
        id: shipment_id,
      },
    })
      .then(async () => {
        toast({
          duration: 2500,
          position: "bottom",
          render: () =>
            MessageSuccess({
              title: "Bien hecho!",
              description: "El envío se actualizó exitosamente!.",
            }),
        });
        sales();
      })
      .catch((error) => {
        if (error.message !== "Unauthorized") {
          toast({
            duration: 2500,
            position: "top",
            render: () =>
              MessageError(
                "Ocurrió un error al actualizar su envío. Por favor intente nuevamente."
              ),
          });
        } else {
          unauthorized();
        }
      })
      .finally(() => {
        setloadingButtons(false);
      });
  };

  return (
    <>
      {loading ? (
        <Center>
          <Spinner />
        </Center>
      ) : (
        <>
          <Flex flexDirection={"column"}>
            <Tabs isFitted variant="enclosed">
              <TabList mb="1em">
                <Tab>Todos los ventas</Tab>
                <Tab>Ventas sin envío</Tab>
                <Tab>Ventas con pagos pendientes</Tab>
              </TabList>
              <TabPanels>
                <TabPanel>
                  {dataSales.length ? (
                    <>
                      <TableSales
                        loadingButtonsOp={loadingButtons}
                        dataSales={dataSales}
                        onClickDeleteShipment={(shipment_id) => {
                          setShipmentToDelete(shipment_id);
                          onOpen();
                        }}
                        onClickEditShipment={(shipment) => {
                          setshipmentToEdit(shipment);
                          setFormAddShipmentVisible(true);
                        }}
                        onClickAddShipment={(sale_id) => {
                          setSaleId(sale_id);
                          setFormAddShipmentVisible(true);
                        }}
                        onClickCeckStatusPay={(sale_id) => {
                          reanalyzePay(sale_id);
                        }}
                        onClickViewProducts={(products) => {
                          setProductsSales(products);
                          setModalViewProductVisible(true);
                        }}
                      />
                    </>
                  ) : (
                    <>
                      <Center>
                        <>
                          <p>
                            <Tag>NO TIENES VENTAS ACTIVAS &#128532;</Tag>
                          </p>
                        </>
                      </Center>
                    </>
                  )}
                </TabPanel>
                <TabPanel>
                  {dataSalesWithoutShipment.length ? (
                    <>
                      <TableSales
                        loadingButtonsOp={loadingButtons}
                        dataSales={dataSalesWithoutShipment}
                        onClickDeleteShipment={(shipment_id) => {
                          setShipmentToDelete(shipment_id);
                          onOpen();
                        }}
                        onClickEditShipment={(shipment) => {
                          setshipmentToEdit(shipment);
                          setFormAddShipmentVisible(true);
                        }}
                        onClickAddShipment={(sale_id) => {
                          setSaleId(sale_id);
                          setFormAddShipmentVisible(true);
                        }}
                        onClickCeckStatusPay={(sale_id) => {
                          reanalyzePay(sale_id);
                        }}
                        onClickViewProducts={(products) => {
                          setProductsSales(products);
                          setModalViewProductVisible(true);
                        }}
                      />
                    </>
                  ) : (
                    <>
                      <Center>
                        <>
                          <p>
                            <Tag>NO TIENES VENTAS RECHAZADAS &#128532;</Tag>
                          </p>
                        </>
                      </Center>
                    </>
                  )}
                </TabPanel>
                <TabPanel>
                  {dataSalesPending.length ? (
                    <>
                      <TableSales
                        loadingButtonsOp={loadingButtons}
                        dataSales={dataSalesPending}
                        onClickDeleteShipment={(shipment_id) => {
                          setShipmentToDelete(shipment_id);
                          onOpen();
                        }}
                        onClickEditShipment={(shipment) => {
                          setshipmentToEdit(shipment);
                          setFormAddShipmentVisible(true);
                        }}
                        onClickAddShipment={(sale_id) => {
                          setSaleId(sale_id);
                          setFormAddShipmentVisible(true);
                        }}
                        onClickCeckStatusPay={(sale_id) => {
                          reanalyzePay(sale_id);
                        }}
                        onClickViewProducts={(products) => {
                          setProductsSales(products);
                          setModalViewProductVisible(true);
                        }}
                      />
                    </>
                  ) : (
                    <>
                      <Center>
                        <>
                          <p>
                            <Tag>
                              NO TIENES VENTAS CON PAGOS PENDIENTES &#128532;
                            </Tag>
                          </p>
                        </>
                      </Center>
                    </>
                  )}
                </TabPanel>
              </TabPanels>
            </Tabs>
          </Flex>
        </>
      )}
      <Modal
        onClose={() => {
          setModalViewProductVisible(false);
        }}
        isOpen={modalViewProductVisible}
        size={"lg"}
      >
        <ModalOverlay />
        <ModalContent>
          <ModalCloseButton />
          <ModalBody>
            <Flex
              shadow={"inner"}
              direction={"column"}
              p={4}
              borderRadius="lg"
              borderColor={colorMode === "light" ? "gray.200" : "gray.700"}
              borderWidth={"1px"}
              h="80vh"
              boxShadow={"inner"}
            >
              <VStack
                spacing={"5px"}
                direction={"row"}
                overflowY="auto"
                className="container-line-cart"
                p={2}
              >
                {productsSales.length ? (
                  <>
                    {productsSales?.map((product) => (
                      <>
                        <Box
                          p={4}
                          display={"flex"}
                          flexDirection={{ base: "column", sm: "row" }}
                          alignItems={"center"}
                          minH={"500px"}
                          w="100%"
                          borderColor={
                            colorMode === "light" ? "gray.800" : "gray.100"
                          }
                          justifyContent="center"
                          h="auto"
                        >
                          <Flex
                            justifyContent={"center"}
                            direction={{ base: "column" }}
                            alignItems="center"
                          >
                            {product.product.url_image ? (
                              <Image
                                boxSize="150px"
                                objectFit="cover"
                                src={product.product.url_image}
                                alt="Dan Abramov"
                              />
                            ) : null}

                            <Text my={5} fontWeight={"bold"}>
                              {product.product.name}
                            </Text>
                            <Text my={5} fontWeight={"bold"}>
                              Precio unitario de venta
                            </Text>
                            <Text>${product.unitary_total}</Text>
                            <Text my={5} fontWeight={"bold"}>
                              Cantidad
                            </Text>
                            <Text>{product.quantity}</Text>
                          </Flex>
                          <Box
                            display={"flex"}
                            alignItems="center"
                            justifyContent={"center"}
                            flexDirection={{ base: "column", sm: "row" }}
                          >
                            <Text
                              mb={{ base: "5px", sm: "0px" }}
                              mr={{ base: "0px", sm: "10px" }}
                              fontWeight={"bold"}
                            >
                              {" "}
                            </Text>
                          </Box>
                        </Box>
                        <Divider />
                      </>
                    ))}
                  </>
                ) : (
                  <>
                    <p>
                      <Tag>NO PUDIMOS MOSTRARTE LOS PRODUCTOS &#128532;</Tag>
                    </p>
                  </>
                )}
              </VStack>
            </Flex>
          </ModalBody>
        </ModalContent>
      </Modal>

      <Modal
        onClose={() => {
          setshipmentToEdit(undefined);
          setFormAddShipmentVisible(false);
        }}
        isOpen={formAddShipmentVisible}
        size={"lg"}
      >
        <ModalOverlay />
        <ModalContent p={8}>
          <ModalCloseButton />
          <ModalBody>
            <Heading mb={0} as={"h4"}>
              {shipmentToEdit
                ? "Editar fecha de envío"
                : "Agregar fecha de envío"}
            </Heading>
            <form
              onSubmit={handleSubmit(
                shipmentToEdit ? onSubmitUpdateShipment : onSubmitCreateShipment
              )}
            >
              <FormControl isInvalid={errors.shipment_date}>
                <FormLabel>Fecha de envío</FormLabel>
                <Input
                  type={"date"}
                  placeholder="Seleccione fecha de envío"
                  defaultValue={
                    shipmentToEdit
                      ? moment(shipmentToEdit.shipment_date).format(
                          "DD/MM/YYYY"
                        )
                      : moment(new Date()).format("DD/MM/YYYY")
                  }
                  id="shipment_date"
                  {...register("shipment_date", {
                    required: "Debe seleccionar una fecha",
                  })}
                />
                <FormErrorMessage>
                  {errors.shipment_date && errors.shipment_date.message}
                </FormErrorMessage>
              </FormControl>
              <Button w={"100%"} mt={3} type="submit">
                Guardar
              </Button>
            </form>
          </ModalBody>
        </ModalContent>
      </Modal>

      <AlertDialog
        isOpen={isOpen}
        leastDestructiveRef={cancelRef}
        onClose={() => {
          onCloseWithCleanState();
        }}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Eliminar envío
            </AlertDialogHeader>

            <AlertDialogBody>
              ¿Seguro que desea eliminar el envío?
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button
                ref={cancelRef}
                onClick={() => {
                  onCloseWithCleanState();
                }}
              >
                Cancelar
              </Button>
              <Button
                colorScheme="red"
                onClick={() => {
                  if (shipmentToDelete) {
                    onSubmitDeleteShipment(shipmentToDelete);
                  }
                  onCloseWithCleanState();
                }}
                ml={3}
              >
                Eliminar
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
};
